/** PRD §F1 seed category list. Copied into `categories` per account at first
 *  login, then editable — this constant is the seed, not the source of truth. */
export const SEED_CATEGORIES = [
  'Photographer',
  'Videographer',
  'Florist',
  'Caterer',
  'Venue',
  'Baker',
  'DJ',
  'Band',
  'Hair & Makeup',
  'Officiant',
  'Rentals',
  'Stationery',
  'Transportation',
  'Lighting',
  'Planner/Assistant',
] as const;

export const INQUIRY_STATUSES = [
  'draft',
  'sent',
  'replied',
  'quote_received',
  'contract_signed',
  'booked',
  'declined',
  'passed',
  'no_response',
] as const;

export type InquiryStatus = (typeof INQUIRY_STATUSES)[number];

export const WEDDING_STATUSES = ['lead', 'active', 'completed', 'cancelled'] as const;
export type WeddingStatus = (typeof WEDDING_STATUSES)[number];

export const INQUIRY_STATUS_LABELS: Record<InquiryStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  replied: 'Replied',
  quote_received: 'Quote received',
  contract_signed: 'Contract signed',
  booked: 'Booked',
  declined: 'Declined',
  passed: 'Passed',
  no_response: 'No response',
};

/**
 * Rank within the linear pipeline. The three off-ramps (`declined`, `passed`,
 * `no_response`) are terminal and deliberately absent — they are not "further
 * along" than anything, so comparing them by rank is a category error. Callers
 * use `isTerminalStatus` first.
 */
export const PIPELINE_RANK: Record<string, number> = {
  draft: 0,
  sent: 1,
  replied: 2,
  quote_received: 3,
  contract_signed: 4,
  booked: 5,
};

export const TERMINAL_STATUSES = ['declined', 'passed', 'no_response'] as const;

export function isTerminalStatus(status: InquiryStatus): boolean {
  return (TERMINAL_STATUSES as readonly string[]).includes(status);
}

/**
 * PRD §F6: "Inference never advances past `quote_received` without human
 * confirmation — contract and booking states are too consequential to
 * auto-set." This is the ceiling the inference engine checks against.
 */
export const MAX_INFERRED_STATUS: InquiryStatus = 'quote_received';

/**
 * PRD §F3 MVP merge-variable set. The composer rejects anything outside it.
 *
 * The wedding's overall budget is deliberately not in here. It is the couple's
 * private number, a single vendor has no need for it, and a vendor reading it
 * as *their* budget overstates their share by the entire rest of the wedding.
 * Vendors get asked for their own pricing instead.
 */
export const MERGE_VARIABLES = [
  'vendor_name',
  'vendor_business',
  'couple_names',
  'wedding_date',
  'wedding_hours',
  /**
   * The recipient's own coverage window — `'11:00 AM – 8:00 PM'`, the hours
   * this one vendor is booked for, which is not the same span as
   * `wedding_hours` above.
   *
   * The only variable in this list resolved **per recipient** while still
   * describing the wedding rather than the vendor: it is read from the
   * recipient's own `primaryCategory` against the wedding's coverage windows,
   * so one batch built from one template sends the photographer their window
   * and the florist theirs. That is why `ComposePage` sets it in the
   * per-vendor context inside `composed` and *not* in `baseContext` — every
   * other wedding-level value is the same string for everyone in the batch,
   * and putting this one there would mail a florist the photographer's hours.
   *
   * A vendor whose category has no window — or who has no category at all —
   * gets no value, and nothing special happens: `renderTemplate` treats it
   * like any other unresolved variable, leaves the token visible and blocks
   * the send. That is the intended outcome, because a vendor reading "you're
   * booked {{coverage_hours}}" is worse than the planner being stopped and
   * asked to draw the window first.
   */
  'coverage_hours',
  'venue',
  'guest_count',
  'planner_name',
  'planner_business',
] as const;

export type MergeVariable = (typeof MERGE_VARIABLES)[number];

/**
 * The variables that fill in per message rather than per wedding.
 *
 * A composer previewing a template has only the wedding to merge against — it
 * does not know who the batch is going to yet — so these come back unresolved
 * every time, and a "still blank" warning listing them would flag the normal
 * case as a problem. The pick step filters them out on the way to that caption;
 * the real gate is the per-message issues computed once each recipient is known.
 *
 * Named here, as a set, rather than inferred from the `vendor_` prefix the
 * composer used to test. That prefix was never the rule — it was a proxy for it
 * that held only while every per-recipient variable happened to be about the
 * vendor's own details. `coverage_hours` ends that: it is read from the
 * recipient's category and so fills in per message, while describing the
 * wedding and carrying no prefix to match. Left on the prefix test, the caption
 * tells a planner whose every vendor has a window that coverage hours are
 * "still blank" and to go fill them in on the wedding, where they already are.
 */
export const PER_RECIPIENT_MERGE_VARIABLES: readonly MergeVariable[] = [
  'vendor_name',
  'vendor_business',
  'coverage_hours',
];

/** Whether a variable resolves per recipient, and so cannot be previewed per wedding. */
export function isPerRecipientVariable(name: string): boolean {
  return (PER_RECIPIENT_MERGE_VARIABLES as readonly string[]).includes(name);
}

/**
 * What each variable is called in the editor's palette.
 *
 * `{{vendor_name}}` is the wire format, not a word anyone thinks in. A planner
 * writing an email is looking for "vendor name", so that is what the palette
 * offers; the token itself stays the thing that gets stored and merged.
 */
export const MERGE_VARIABLE_LABELS: Record<MergeVariable, string> = {
  vendor_name: 'Vendor name',
  vendor_business: 'Vendor business',
  couple_names: 'Couple names',
  wedding_date: 'Wedding date',
  wedding_hours: 'Wedding hours',
  coverage_hours: 'Coverage hours',
  venue: 'Venue',
  guest_count: 'Guest count',
  planner_name: 'Your name',
  planner_business: 'Your business',
};

/**
 * Gmail's documented daily send caps. Consumer accounts are the binding
 * constraint: a planner working through several weddings in one sitting can
 * genuinely approach 500, and hitting it mid-batch is the failure mode PRD §F3
 * calls out as needing graceful degradation rather than an error.
 */
export const SEND_CAPS = {
  consumer: 500,
  workspace: 2000,
} as const;

/** Stagger window between messages in a batch, in seconds (PRD §F3). */
export const SEND_STAGGER_MIN_SECONDS = 20;
export const SEND_STAGGER_MAX_SECONDS = 60;

export const MAX_BATCH_SIZE = 25;

/** Gmail label prefix. Nesting in Gmail is a naming convention, not structure. */
export const LABEL_PREFIX = 'VendorOps';

/** Below this, a Tier 4 classifier result goes to the review queue instead. */
export const MATCH_CONFIDENCE_THRESHOLD = 0.75;
